import React, { useState } from 'react';
import { Avatar, Dropdown, Menu, Modal } from 'antd';
import { connect } from 'react-redux';
import { HeartOutlined, LogoutOutlined, UserSwitchOutlined } from '@ant-design/icons';
import { signOut } from 'redux/actions/Auth';
import { AccountUser, User } from '@quick/client';
import { Link } from 'react-router-dom';
import { auth } from '../../auth/firebaseAuth';
import { getNameAvatarImage } from '../../utils/HoaUtils';
import { useCurrentAccountUser, useCurrentUser } from '../../redux/selector';
import { useAccountPrefixPath } from '../../hooks/vendorlink/Routing';
import { displayName } from '../../utils/vendorlink/AccountUserUtils';
import { COMMUNITY_PREFIX_PATH } from '../../configs/AppConfig';
import Text from 'antd/lib/typography/Text';
import { env } from '../../configs/EnvironmentConfig';

interface MenuItem {
  title: string;
  icon: React.ReactNode;
  path: string;
  onClick?: () => void;
}

const getMenuItems = (appPrefixPath: string, onSignOutClick: () => void): MenuItem[] => {
  return [
    {
      title: 'Get help',
      icon: <HeartOutlined />,
      path: `#`,
      onClick: () => Intercom('show'),
    },
    ...(env.REACT_APP_ENV === 'local'
      ? [
          {
            title: 'Switch to community',
            icon: <UserSwitchOutlined />,
            path: COMMUNITY_PREFIX_PATH,
          },
        ]
      : []),
    {
      title: 'Sign out',
      icon: <LogoutOutlined />,
      path: `#`,
      onClick: onSignOutClick,
    },
  ];
};

interface UserInfoProps {
  user: User;
  showText?: boolean;
}

// Used when there is no account.
const UserInfo = ({ user, showText = false }: UserInfoProps) => {
  const name = 'New User';
  const profileImg = user.imageUrl ?? getNameAvatarImage(name);

  if (!showText) {
    return <Avatar src={profileImg} />;
  }

  return (
    <div className="d-flex">
      <Avatar className="flex-shrink-0" size={45} src={profileImg} />
      <div className="pl-3">
        <div className="mb-0 label-l">{name}</div>
      </div>
    </div>
  );
};

interface AccountUserProps {
  accountUser: AccountUser;
  showText?: boolean;
}

const AccountUserInfo = ({ accountUser, showText = false }: AccountUserProps) => {
  const { email, imageUrl } = accountUser;
  const memberDescription = email;

  if (!showText) {
    return <Avatar src={imageUrl} />;
  }

  return (
    <div className="d-flex">
      <Avatar className="flex-shrink-0" size={45} src={imageUrl} />
      <div className="pl-3 flex-shrink-1 d-flex flex-column" style={{ minWidth: 0 }}>
        <div className="mb-0 label-l">{displayName(accountUser)}</div>
        <Text className="text-content-secondary" ellipsis>
          {memberDescription}
        </Text>
      </div>
    </div>
  );
};

interface NavProfileProps {
  signOut: () => void;
}

export const NavProfile = ({ signOut }: NavProfileProps) => {
  const user = useCurrentUser()!;
  const accountUser = useCurrentAccountUser();

  const [signOutModalVisible, setSignOutModalVisible] = useState(false);
  const onSignOut = async () => {
    await auth.signOut();
    signOut();
  };

  const onMenuVisible = () => {
    // TODO: preload sub-pages if needed.
  };
  const appPrefixPath = useAccountPrefixPath();
  let menuItems = getMenuItems(appPrefixPath, () => setSignOutModalVisible(true));
  const profileMenu = (
    <div className="nav-profile nav-dropdown">
      <div className="nav-profile-header">
        {accountUser ? <AccountUserInfo showText accountUser={accountUser} /> : <UserInfo showText user={user} />}
      </div>
      <div className="nav-profile-body">
        <Menu selectable={false}>
          {menuItems.map((el, i) => {
            return (
              <Menu.Item key={i} onClick={el.onClick}>
                <div
                  // For Intercom product tour. Construct an ID from non-word characters.
                  id={`profile-nav-${el.title.replaceAll(/(\W)/g, '-').toLowerCase()}`}
                  className="d-flex align-items-center h-100"
                >
                  <span className="mr-3 d-inline-flex align-items-center">{el.icon}</span>
                  <span className="label-s">{el.title}</span>
                  <Link to={el.path ?? '#'} />
                </div>
              </Menu.Item>
            );
          })}
        </Menu>
      </div>
      <Modal
        visible={signOutModalVisible}
        onOk={() => onSignOut()}
        okText="Sign out"
        okType="danger"
        onCancel={() => {
          setSignOutModalVisible(false);
        }}
        cancelButtonProps={{ type: 'text' }}
        closable={false}
        maskClosable
        transitionName=""
        maskTransitionName=""
        width={300}
      >
        <div className="heading-xs">Are you sure?</div>
        <div className="mt-3">You can always log back in at any time.</div>
      </Modal>
    </div>
  );
  return (
    <Dropdown placement="bottomRight" overlay={profileMenu} onVisibleChange={onMenuVisible}>
      <Menu id="profile-nav" className="d-flex align-item-center" mode="horizontal">
        <Menu.Item className="p-0">
          {accountUser ? <AccountUserInfo accountUser={accountUser} /> : <UserInfo user={user} />}
        </Menu.Item>
      </Menu>
    </Dropdown>
  );
};

export default connect(null, { signOut })(NavProfile);
